import { DomainForm } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useGetClients } from "@/hooks/clients";
import { useGetAllHotelRooms } from "@/hooks/hotel-rooms";
import { T } from "@/i18n";
import { dateToString } from "@/lib/utils";
import {
  Accommodation,
  AccommodationStateAsArray,
  CreateAccommodation,
  Hotel,
  HotelRoom,
} from "@/models";
import { useForm } from "@deadcow-enterprises/react-forms";
import { Pencil, Plus, Trash } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReservationFormContext } from "../contex";
import { useGetHotels } from "@/hooks/hotels";
import { useGetHotelRoomPrices } from "@/hooks/hote-room-prices/get.hooks";

export function Accommodations({
  day,
  reservationId,
  accommodations,
}: {
  day: number;
  reservationId: number;
  accommodations: Accommodation[];
}) {
  const { scheduleManager } = useReservationFormContext();
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          <T tKey="reservations.form.schedule.accommodations.title" />
        </span>
        <UpsertCard
          day={day}
          reservationId={reservationId}
          onUpsert={async (model) => {
            scheduleManager.handleNewAccommodation(day, model);
          }}
        >
          <Button size="sm" variant="text" className="m-1">
            <Plus />
            <T tKey="reservations.form.schedule.accommodations.new" />
          </Button>
        </UpsertCard>
      </div>
      <div className="grid gap-2">
        {accommodations.map((acc) => (
          <AccommodationCard
            accommodation={acc}
            key={acc.id}
            reservationId={reservationId}
          />
        ))}
      </div>
    </div>
  );
}

function AccommodationCard({
  accommodation,
  reservationId,
}: {
  accommodation: Accommodation;
  reservationId: number;
}) {
  const { data: clients, isLoading: clientsLoading } = useGetClients();
  const { scheduleManager } = useReservationFormContext();
  const { t } = useTranslation();
  const { data: rooms, isLoading: roomsLoading } = useGetAllHotelRooms();
  const name = useMemo(() => {
    const room = rooms?.find(
      (v) => v.id === accommodation.hotelRoomId?.toString(),
    );
    if (!room) return null;
    return `${room.hotelName} ${room.designation ? `( ${room.designation} )` : ""}`;
  }, [rooms, accommodation.hotelRoomId]);
  return (
    <Card className="w-full">
      <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
        <div className="grid gap-1">
          {roomsLoading ? (
            <Skeleton className="h-5 w-1/5" />
          ) : (
            <span className="font-semibold">{name ?? ""}</span>
          )}
          <span className="text-primary">
            <T
              tKey="reservations.form.schedule.accommodations.occupants.count"
              args={{
                count: accommodation.reservationClients.length.toString(),
              }}
            />
          </span>
        </div>
        <div>
          <UpsertCard
            day={accommodation.day}
            reservationId={reservationId}
            onUpsert={async (model) => {
              scheduleManager.handleUpdateAccommodation(accommodation.day, {
                ...model,
                id: accommodation.id,
              });
            }}
          >
            <Button variant="ghost" size="icon" className="m-1">
              <Pencil />
              <span className="sr-only">
                <T tKey="reservations.form.schedule.accommodations.edit" />
              </span>
            </Button>
          </UpsertCard>
          <Button
            variant="text-destructive"
            size="icon"
            className="m-1"
            onClick={() =>
              scheduleManager.handleDeleteAccommodation(
                accommodation.day,
                accommodation.id,
              )
            }
          >
            <Trash />
            <span className="sr-only">
              <T tKey="reservations.form.schedule.accommodations.delete" />
            </span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid gap-1">
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.requestedType" />
            {": "}
          </span>
          {t(
            accommodation.requestedType
              ? `reservations.form.schedule.accommodations.requestedTypes.${accommodation.requestedType}`
              : "-",
          )}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.state" />
            {": "}
          </span>
          {t(
            accommodation.state
              ? `reservations.form.schedule.accommodations.states.${accommodation.state}`
              : "-",
          )}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.childrenCount" />
            {": "}
          </span>
          {accommodation.childrenCount ?? "-"}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.headCount" />
            {": "}
          </span>
          {accommodation.headCount ?? "-"}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.expenses" />
            {": "}
          </span>
          {accommodation.expenses ?? "-"}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.numberOfBeds" />
            {": "}
          </span>
          {accommodation.numberOfBeds ?? "-"}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.price" />
            {": "}
          </span>
          {accommodation.hotelRoomPrice?.price?.toString().concat(" €") ?? "-"}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.invoiceNumber" />
            {": "}
          </span>
          {accommodation.invoiceNumber ?? "-"}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.release" />
            {": "}
          </span>
          {dateToString(accommodation.release)}
        </span>
        <span className="break-all pl-1 text-muted-foreground">
          <span className="font-medium text-card-foreground">
            <T tKey="reservations.form.schedule.accommodations.fields.notes" />
            {": "}
          </span>
          {accommodation.notes ?? "-"}
        </span>

        <div className="mt-4 flex justify-between gap-2">
          <span className="font-medium text-primary">
            <T tKey="reservations.form.schedule.accommodations.occupants.title" />
          </span>
          <AddClient accommodationId={accommodation.id} day={accommodation.day}>
            <Button size="sm" variant="text" className="m-1">
              <Plus />
              <T tKey="reservations.form.schedule.accommodations.occupants.add" />
            </Button>
          </AddClient>
        </div>
        {accommodation.reservationClients.map((rc) => (
          <div className="flex items-center gap-2" key={rc.clientId}>
            <Button
              size="sm"
              variant="text-destructive"
              className="m-1"
              onClick={() => {
                const acc = {
                  ...accommodation,
                  reservationClients: accommodation.reservationClients.filter(
                    (c) => c.clientId !== rc.clientId,
                  ),
                };
                scheduleManager.handleUpdateAccommodation(acc.day, acc);
              }}
            >
              <Trash />
            </Button>
            <span className="font-medium text-muted-foreground">
              {clientsLoading || !clients ? (
                <Skeleton className="h-3 w-full" />
              ) : (
                clients?.find((c) => c.id === rc.clientId.toString())?.name
              )}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AddClient({
  day,
  children,
  accommodationId,
}: {
  day: number;
  children?: ReactNode;
  accommodationId: string;
}) {
  const [open, setOpen] = useState(false);
  const { reservationClients, days, scheduleManager } =
    useReservationFormContext();

  const { data: clientsDetails, isLoading: clientsDetailsloading } =
    useGetClients();

  const invalidClientIds = useMemo(() => {
    return (
      days
        .find((d) => d.reservationDay.value.day === day)
        ?.accommodations.filter((a) => a.value.id !== accommodationId)
        .map((a) => a.value.reservationClients)
        .flat()
        .map((rc) => rc.clientId) ?? []
    );
  }, [accommodationId, day, days]);

  const accommodation = useMemo(
    () =>
      days
        .find((d) => d.reservationDay.value.day === day)!
        .accommodations.find((a) => a.value.id === accommodationId)!.value,
    [accommodationId, day, days],
  );

  const clientsOfDay = useMemo(
    () =>
      days
        .find((d) => d.reservationDay.value.day === day)
        ?.accommodations.map((a) => a.value.reservationClients)
        .flat()
        .map((c) => c.clientId),
    [day, days],
  );
  const clients = useMemo(
    () => reservationClients.filter((c) => !clientsOfDay?.includes(c.clientId)),
    [clientsOfDay, reservationClients],
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        {!clientsDetails || clientsDetailsloading ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <SelectSearch
            options={clients
              .filter((c) => !invalidClientIds.includes(c.clientId))
              .map((c) => ({
                label: clientsDetails.find(
                  (cd) => cd.id === c.clientId.toString(),
                )!.name,
                value: c.clientId,
              }))}
            value={undefined}
            onChange={(v) => {
              const acc = {
                ...accommodation,
                reservationClients: [...accommodation.reservationClients],
              };
              acc.reservationClients.push({
                accommodationId: Number.parseInt(accommodationId),
                clientId: v,
                reservationId: accommodation.reservationId!,
              });
              scheduleManager.handleUpdateAccommodation(day, acc);
              setOpen(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function UpsertCard({
  day,
  reservationId,
  onUpsert,
  model,
  children,
}: {
  day: number;
  reservationId: number;
  model?: CreateAccommodation;
  onUpsert: (v: CreateAccommodation) => Promise<void>;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data: hotels, isLoading: hotelsLoading } = useGetHotels();
  const { data: rooms, isLoading: roomsLoading } = useGetAllHotelRooms();
  const isLoading = useMemo(() => {
    return hotelsLoading && roomsLoading;
  }, [hotelsLoading, roomsLoading]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="max-h-[520px] w-80 overflow-y-scroll"
        side="left"
      >
        {!isLoading && hotels && rooms && (
          <Form
            model={model}
            onUpsert={async (v) => {
              await onUpsert(v);
              if (!model) setOpen(false);
            }}
            day={day}
            reservationId={reservationId}
            hotels={hotels ?? []}
            rooms={rooms}
          />
        )}
        {isLoading && <Skeleton className="h-full w-full" />}
      </PopoverContent>
    </Popover>
  );
}

function Form({
  day,
  reservationId,
  onUpsert,
  model,
  hotels,
  rooms,
}: {
  day: number;
  reservationId: number;
  rooms: HotelRoom[];
  model?: CreateAccommodation;
  hotels: Hotel[];
  onUpsert: (v: CreateAccommodation) => Promise<void>;
}) {
  const { t } = useTranslation();

  //TODO: Get HotelRoomPrices here or get All HotelRoomPrices
  const form = useForm<CreateAccommodation & { hotelId?: number }>({
    day: {
      value: model?.day ?? day,
    },
    hotelId: {
      value: model?.hotelRoomId
        ? rooms.find((r) => Number.parseInt(r.id) === model.hotelRoomId)!
            .hotelId!
        : undefined,
    },
    requestedType: {
      value: model?.requestedType,
    },
    state: {
      value: model?.state,
    },
    childrenCount: {
      value: model?.childrenCount,
    },
    headCount: {
      value: model?.headCount,
    },
    hotelRoomId: {
      value: model?.hotelRoomId,
    },
    expenses: {
      value: model?.expenses,
    },
    invoiceNumber: {
      value: model?.invoiceNumber,
    },
    numberOfBeds: {
      value: model?.numberOfBeds,
    },
    hotelRoomPriceId: {
      value: model?.hotelRoomPriceId,
    },
    release: {
      value: model?.release,
    },
    notes: {
      value: model?.notes,
    },
    reservationClients: {
      value: model?.reservationClients ?? [],
    },
    reservationId: {
      value: model?.reservationId ?? reservationId,
    },
  });
  const { data: prices, isLoading: pricesLoading } = useGetHotelRoomPrices(
    form.value.hotelRoomId?.toString() ?? "-1",
    {
      enabled: !!form.value.hotelRoomId,
    },
  );
  return (
    <DomainForm
      form={form}
      onUpsert={onUpsert}
      buttonTranslation={
        !model
          ? "reservations.form.schedule.accommodations.create"
          : "reservations.form.schedule.accommodations.update"
      }
      fields={[
        {
          key: "hotelId",
          label: "reservations.form.schedule.accommodations.fields.hotelId",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.hotelId?.toString() ?? undefined}
              onChange={(v) => {
                form.setValue("hotelId", Number.parseInt(v));
              }}
              options={hotels.map((v) => ({
                value: v.id,
                label: v.name,
              }))}
            />
          ),
        },
        {
          key: "hotelRoomId",
          label: "reservations.form.schedule.accommodations.fields.hotelRoomId",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.hotelRoomId?.toString() ?? undefined}
              onChange={(v) => {
                form.setValue("hotelRoomId", Number.parseInt(v));
              }}
              options={rooms
                .filter((r) => r.hotelId === (form.value.hotelId ?? -1))
                .map((v) => ({
                  value: v.id,
                  label: `${v.hotelName} ${v.designation ? `( ${v.designation} )` : ""}`,
                }))}
              disabled={!form.value.hotelId}
            />
          ),
        },
        {
          key: "hotelRoomPriceId",
          label:
            "reservations.form.schedule.accommodations.fields.requestedType",
          input: (form, id) =>
            pricesLoading || !prices ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.hotelRoomPriceId}
                onChange={(v) => {
                  form.setValue("hotelRoomPriceId", v);
                }}
                disabled={!form.value.hotelRoomId}
                options={prices
                  .filter((p) => p.hotelRoomId === form.value.hotelRoomId)
                  .map((v) => ({
                    value: v.id,
                    label: v.type?.toString() ?? "",
                  }))}
              />
            ),
        },
        {
          key: "state",
          label: "reservations.form.schedule.accommodations.fields.state",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="max-h-[320px] w-full overflow-y-scroll"
              value={form.value.state}
              onChange={(v) => {
                form.setValue("state", v);
              }}
              options={AccommodationStateAsArray.map((v) => ({
                value: v,
                label: t(
                  `reservations.form.schedule.accommodations.states.${v}`,
                ),
              }))}
            />
          ),
        },
        {
          key: "childrenCount",
          label:
            "reservations.form.schedule.accommodations.fields.childrenCount",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.childrenCount ?? ""}
              type="number"
              onChange={(e) => {
                form.setValue("childrenCount", Number.parseInt(e.target.value));
              }}
            />
          ),
        },
        {
          key: "headCount",
          label: "reservations.form.schedule.accommodations.fields.headCount",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.headCount ?? ""}
              type="number"
              onChange={(e) => {
                form.setValue("headCount", Number.parseInt(e.target.value));
              }}
            />
          ),
        },
        {
          key: "expenses",
          label: "reservations.form.schedule.accommodations.fields.expenses",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.expenses ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue("expenses", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          key: "numberOfBeds",
          label:
            "reservations.form.schedule.accommodations.fields.numberOfBeds",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.numberOfBeds ?? ""}
              type="number"
              onChange={(e) => {
                form.setValue("numberOfBeds", Number.parseInt(e.target.value));
              }}
            />
          ),
        },

        {
          key: "invoiceNumber",
          label:
            "reservations.form.schedule.accommodations.fields.invoiceNumber",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.invoiceNumber ?? ""}
              onChange={(e) => {
                form.setValue("invoiceNumber", e.target.value);
              }}
            />
          ),
        },
        {
          key: "release",
          label: "reservations.form.schedule.accommodations.fields.release",
          input: (form, id) => (
            <DatePicker
              id={id}
              date={form.value.release ?? undefined}
              setDate={(d) => form.setValue("release", d)}
            />
          ),
        },
        {
          key: "notes",
          label: "reservations.form.schedule.accommodations.fields.notes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.notes ?? ""}
              onChange={(e) => {
                form.setValue("notes", e.target.value);
              }}
            />
          ),
        },
      ]}
    />
  );
}
